import React, { useState, useMemo, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useDispatch, useSelector, useI18n, useLogger, useStatusOfDay, useLicenses } from '~Hooks';
import styled, { keyframes, css } from 'styled-components';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import { useLocation } from 'react-router';

import Journal from '~Utils/journal';
import { LocalVariation } from '~Types';
import Cart from '~Scenes/POS/Components/Cart';
import CountSelector from './Components/CountSelector';

import * as actions from '~actions';

import { DISCOUNT_TYPE, ORDER_STATUS, SERVICE_TYPE } from '~../__queries__/globalTypes';
import { generateID } from '~reducers';
import SystemStatus from '~Components/SystemStatus';
import useBongQuery from '~Scenes/POS/Queries/query';
import useCategories from './categories';
import DayNotStarted from '~Scenes/POS/Components/DayNotStarted';
import LoadingScreen from '~Components/LoadingScreen';
import CategoriesList from './Components/CategoriesList/CategoriesList';
import Sidebar from '~Scenes/POS/Components/Sidebar';
import SearchIcon from '~Assets/icons/search.svg';
import GridButtons from '~Scenes/POS/Components/GridButtons';
import { CompleteBongQuery_venue_company_layouts_categories_page as CategoryPage } from '~Scenes/POS/Queries/__queries__';
import SidebarButtons from '~Scenes/POS/Components/SidebarButtons';
import BottomButtons from '~Scenes/POS/Components/BottomButtons';
import Modal from '~ComponentsImproved/Modal';
import MessageInput from '~ComponentsImproved/MessageInput';
import useModal from '~ComponentsImproved/Modal/useModal';
import LoadingDataScreen from '~ComponentsImproved/LoadingDataScreen';
import PrinterStatus from '~Components/PrinterStatus';
import ItemModificationChange from './Components/ItemModificationChange';
import Breadcrumbs from './Components/Breadcrumbs';
import IconButton from '~ComponentsImproved/IconButton';
import Icon from '~ComponentsImproved/Icon';
import SearchArticles from './Components/SearchArticles';
import { useCart } from '~Components/CartProvider';
import useOrderParking, { OrderWithItems } from '~Hooks/useOrderParking';
import { useTableUniverse } from '~Components/TableUniverse';
import { parseParkedOrderName } from '~Scenes/ParkedOrders/utils';
import { useStorage } from '~Components/Storage';
import { useIncomingAppOrders } from '~Providers/IncomingAppOrdersProvider';
import KDSButtonModal from '~Scenes/EventedKitchenDisplay/Components/KDSButtonModal';
import IncomingOrdersIndicator from '~Scenes/IncomingAppOrders/IncomingOrdersIndicator';
import ParkedOrderPrintsModal from './Components/ParkedOrderPrintsModal';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';

const entryAnim = keyframes`
  0% {
    transform: scale(0.93);
    opacity: 0;
  }

  20% {
    transform: scale(0.93);
    opacity: 0;
  }

  60% {
    transform: scale(1);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const Wrapper = styled.div<{
  yOffset?: number;
  animateOnMount?: boolean;
}>`
  display: flex;
  flex-direction: row;
  top: ${(props) => props.yOffset || 0}px;
  left: 0;
  bottom: 0;
  right: 0;
  overflow: hidden;
  box-sizing: border-box;
  position: fixed;
  z-index: 100;
  background: var(--colorBlueGray1100);
  ${(props) =>
    props.animateOnMount &&
    css`
      animation: ${entryAnim} 450ms cubic-bezier(0, 1.08, 0.37, 0.97);
      animation-fill-mode: forwards;
    `}
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  position: relative;
`;
interface LeftItem {
  id: string;
  quantity: number;
  outOfStock: boolean;
}

export interface SelectedServiceCharge {
  chequeId: string;
  categoryId: string;
  entryId: string;
  itemId: string;
  percentage: number;
  vatRate: number;
  title: string;
  amount: number;
}

interface Props {
  onClose: () => void;
  yOffset?: number;
  tableId?: string | null;
  venueId: string;
  paymentId: string | null;
  serviceType?: SERVICE_TYPE;
  reset?: () => void;
  buttons: ReactNode;
  animateOnMount?: boolean;
  leftItems: LeftItem[];
  enableQuantityRefetch: boolean;
  onEnableQuantityRefetch: (enabled: boolean) => void;
  orderId: string | null;
  alreadyInCartCountMap?: {
    [itemId: string]: number;
  };
  setOrderWasBonged?: (bonged: boolean) => void;
  hasServiceCharge?: boolean;
  isActive?: boolean;
  isTsr?: boolean;
}

const Bong = ({
  onClose,
  buttons,
  reset,
  yOffset = 24,
  tableId,
  venueId,
  animateOnMount,
  serviceType,
  paymentId,
  leftItems,
  orderId,
  alreadyInCartCountMap,
  setOrderWasBonged,
  hasServiceCharge,
  isActive = true,
  isTsr = false,
}: Props) => {
  const [storage] = useStorage();
  const { i18n } = useI18n();
  const logger = useLogger('pos');
  const cart = useCart();

  const orderParking = useOrderParking();
  const tableUniverse = useTableUniverse();
  const licenses = useLicenses();
  const dispatch = useDispatch();
  const reportSnapshot = useSelector((state) => state.reportSnapshot);
  const staffId = useSelector((state) => state.staff?.id);
  const statusOfDay = useStatusOfDay();

  //This is a ref to check if payment has started, to avoid adding items when payment is in process. We use a ref here because we want the latest value in the addItem callback without having to add paymentId to its dependencies
  const isPaymentStartedRef = useRef(paymentId);
  isPaymentStartedRef.current = paymentId;

  const [selectedVariationItemId, setSelectedVariationItemId] = useState<string | null>(null);
  const [count, setCount] = useState(1);
  const [currentActionId, setActionId] = useState(0);
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [subPage, setSubPage] = useState<CategoryPage | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string; name: string }[]>([]);
  const [showModifyModal, setShowModifyModal] = useState<boolean>(false);
  const [restoredParkedOrderMeta, setRestoredParkedOrderMeta] = useState<{
    id: string;
    membershipDiscountPercentage?: number;
  } | null>(null);
  const [newParkedOrderId, setNewParkedOrderId] = useState<string>('');

  const incomingAppOrders = useIncomingAppOrders();

  const country = useSelector((state) => state.country);
  const selectedKDSStation = useSelector((state) => state.stations)?.find(
    (s) => s.id === storage.selectedKDSStation,
  );

  const parkedPrintsModal = useModal();
  const { isShowing: showParkname, toggle: toggleShowParkname } = useModal();
  const { isShowing: showSearchModal, toggle: toggleShowSearchModal } = useModal();

  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const { progress, progressText, data, ...bongQuery } = useBongQuery(venueId);

  const {
    allItems,
    layout,
    mapItem,
    categories,
    layoutCategory,
    selectedCategory,
    setSelectedCategory,
    itemToCategoryMap,
    categoryItems,
  } = useCategories(data, leftItems);

  const hasCourseTypes = useMemo(() => allItems.some((item) => item.courseType), [allItems]);

  useEffect(() => {
    if (!selectedCategory) return;
    setBreadcrumbs([{ id: selectedCategory.id, name: selectedCategory.name }]);
  }, [selectedCategory]);

  const clear = useCallback(() => {
    cart.setItems([]);
    cart.selectedItems.clear();
    cart.removeMessagesFromTicket();
    setSelectedVariationItemId(null);
    cart.setAppliedDiscount(null);
    setOrderWasBonged && setOrderWasBonged(false);
    cart.clearUserData();

    Journal.write('Clear all items from cart');
  }, [cart.items.length]);

  const undo = useCallback(() => {
    if (cart.items.length > 0) {
      Journal.write('Undo Last Item Add');
    }

    cart.setItems(cart.items.filter((item) => item.actionId !== currentActionId));

    const actionId = currentActionId - 1;

    setActionId(actionId);
    cart.selectedItems.clear();
    setSelectedVariationItemId(null);
  }, [currentActionId, cart.items]);

  const handleCategoryClick = useCallback(
    (id: string, name: string) => {
      setSubPage(null);
      setBreadcrumbs([{ id, name }]);

      if (selectedCategory?.id == id) {
        setSubPage(layoutCategory?.page ?? null);
      } else {
        setSelectedCategory({ id, name });
      }
    },
    [layoutCategory, selectedCategory],
  );

  const removeItems = (ids: string[]) => {
    Journal.write('Remove items', ids.length.toString());

    cart.removeItems(ids);
    setSelectedVariationItemId(null);
  };

  const handleParkOrder = async (name: string) => {
    const order = {
      id: orderId!,
      createdAt: new Date().toISOString(),
      staffId,
      items: cart.packagedItems,
      tableId: null,
      venueId,
      serviceType,
      name: name ?? undefined,
      orderEasyId: cart.easyId,
      desiredTime: cart.desiredTime ?? null,
      userId: cart.userId,
    };

    const newlyParkedOrderId = await orderParking.park((order as unknown) as OrderWithItems);

    toggleShowParkname();

    /** ES has their own way of handling printing before park */
    if (country === 'ES' && newlyParkedOrderId) {
      setNewParkedOrderId(newlyParkedOrderId);
      parkedPrintsModal.toggle();
      return;
    }

    cart.setDesiredTime(null);
    reset && reset();
  };

  const addItem = (
    id: string,
    title: string,
    categoryId: string,
    options: {
      variations: LocalVariation[];
      removedVariations: LocalVariation[];
      message?: string | null;
      type?: string;
      count?: number;
      courseType?: any;
      isMessage?: boolean;
      parentId?: string;
      defaultOrderStatus?: ORDER_STATUS | null;
    },
    price?: number,
    itemToRemove?: string,
  ): { cartItemIds: string[] } => {
    const cartItemIds: string[] = [];

    if (paymentId || isPaymentStartedRef.current) {
      dispatch(actions.addToast(i18n('TOAST.Payment.CantAddItems'), true));
      return { cartItemIds };
    }

    // If an item has been changed remove the previous version and connected upsales
    if (itemToRemove) {
      const upsalesToRemove = cart.items
        .filter((item) => item.parentId === itemToRemove)
        .map((item) => item.id);
      removeItems([itemToRemove, ...upsalesToRemove]);
    }

    // Increment the action ID
    const actionId = currentActionId + 1;
    let lastItemId: string | null = null;

    // Used for "undo" features
    setActionId(actionId);

    const item = allItems.find((i: any) => i.id === id)!;

    const [baseCount, fractionCount] = (options?.count ?? count).toString().split('.', 2);

    const createItem = (fraction?: number) => {
      const itemCartId = generateID();
      const type = options.type;

      const { variations, removedVariations, courseType } = options;

      lastItemId = itemCartId;

      Journal.write('Item Added', title);

      const actualTitle = i18n(item.titleLang);
      let isMessage = false;

      if (actualTitle.includes('[m]')) {
        isMessage = true;
      }

      const takeAwayVatRate = itemToCategoryMap[item.id]?.ukTakeAwayVatRate;

      cart.addItem({
        id: itemCartId,
        actionId,
        itemId: id,
        title,
        description: i18n(item.descriptionLang),
        type,
        variations,
        removedVariations,
        message: options.message,
        courseType: courseType,
        vatRate: item.vatRate,
        takeAwayVatRate: takeAwayVatRate,
        fraction,
        categoryId,
        price,
        isMessage,
        parentId: options.parentId === 'parent' ? itemCartId : options.parentId,
        hasModificationGroups: item.upsales.length > 0 || item.modificationGroups.length > 0,
        defaultOrderStatus: options.defaultOrderStatus,
        manualName: item.manualName,
        manualPrice: item.manualPrice,
        volumeSuffix: item.volumeSuffix,
        disableMembershipDiscount: item.disableMembershipDiscount,
      });

      cartItemIds.push(itemCartId);
    };

    if (baseCount) {
      _.range(+baseCount).forEach(() => createItem());
    }

    if (fractionCount && !isNaN(+fractionCount)) {
      const fraction = +`0.${fractionCount}`;
      createItem(fraction);
    }

    // Always reset count after adding an item
    setCount(1);
    cart.selectedItems.clear();
    setSelectedVariationItemId(lastItemId);
    return { cartItemIds };
  };

  const addItems = (ids: string[]) => {
    if (paymentId || isPaymentStartedRef.current) {
      dispatch(actions.addToast(i18n('TOAST.Payment.CantAddItems'), true));
      return;
    }

    ids.forEach((id) => {
      const item = allItems.find((ai: any) => ai.id === id);

      if (!item) {
        return;
      }

      let title = i18n(item.titleLang);
      let isMessage = false;

      if (title.includes('[m]')) {
        title = title.substring(3);
        isMessage = true;
      }

      const categoryId = itemToCategoryMap[item.id]?.id;
      if (!categoryId) {
        logger.warn('Bong->AddItems: Could not find category for item', {
          itemId: item.id,
          itemTitle: item.titleLang,
          categoryMap: {
            force: '123',
            data: itemToCategoryMap,
          },
        });
      }

      addItem(id, title, categoryId ?? '', {
        removedVariations: [],
        variations: [],
        courseType: item.courseType,
        isMessage,
      });
    });
  };

  // Send bong via printer status component
  function sendTestBong() {
    /**
     * If we dont add this, the items are printed like this:
     * undefinedx Item Title
     */
    const itemsWithCount = cart.items.map((item) => ({
      ...item,
      id: uuid(),
      count: 1,
    }));

    const messages = itemsWithCount.filter((item) => item.isMessage);
    const comment = messages.map((message) => message.title).join(', ') || '';

    const dataToSend = {
      type: actions.SEND_TABLE_ITEMS,
      items: itemsWithCount.filter((item) => !item.isMessage),
      order: {
        id: uuid(),
        orderNumber: i18n('PrintType.TEST_PRINT'),
        desiredTime: null,
        coverage: 0,
        staffId: null,
        comment,
      },
      serviceType,
      messages: messages,
    };

    logger.info('Sending bong via printer status', dataToSend);

    dispatch({
      ...dataToSend,
    });
  }

  const addMessageOnLastItem = (message: string) => {
    cart.setItems((state) =>
      state.map((item, index) => {
        const isSelected = cart.selectedItems.ids.includes(item.id);

        // If we have selected an item, update those, otherwise
        // update last item we added to card
        const shouldSet =
          cart.selectedItems.ids.length > 0 ? isSelected : index === state.length - 1;

        if (shouldSet) {
          return {
            ...item,
            message,
          };
        }

        return item;
      }),
    );
  };

  const handleSubPage = (subPage: any) => {
    if (!subPage) {
      setSubPage(null);
      // Set breadcrumbs to the first one which is always the same as the sidebar category
      setBreadcrumbs((prev) => [prev[0]]);
    } else {
      if (!subPage.__stagedBundle) {
        setBreadcrumbs((prev) => [...prev, { ...subPage }]);
      }
      setSubPage(subPage);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index == 0) {
      handleSubPage(null);
    } else {
      setBreadcrumbs(breadcrumbs.slice(0, index));
      handleSubPage(breadcrumbs[index]);
    }
  };

  const variationsItem =
    cart.selectedItems.ids.length === 1
      ? cart.items.find((i) =>
          cart.selectedItems.ids.length === 1
            ? i.id === cart.selectedItems.ids[0]
            : i.id === selectedVariationItemId,
        )!
      : null;

  /**
   * Restores parked order
   */
  useEffect(() => {
    const id = searchParams.get('parkedOrderId');
    const membershipDiscount = searchParams.get('membershipDiscount');
    if (id) {
      setRestoredParkedOrderMeta({
        id,
        membershipDiscountPercentage: membershipDiscount
          ? parseFloat(membershipDiscount)
          : undefined,
      });
    }
  }, [searchParams]);

  // Handle parked order restoration
  useEffect(() => {
    if (!restoredParkedOrderMeta?.id || bongQuery.loading || allItems.length === 0 || !isActive) {
      return;
    }

    const cheque = tableUniverse.cheques.find((c) => c.id === restoredParkedOrderMeta?.id);
    const parsedOrder = parseParkedOrderName(cheque?.name ?? '');
    const orderServiceType = (parsedOrder.serviceType as SERVICE_TYPE) ?? SERVICE_TYPE.EAT_HERE;
    const orderItems = tableUniverse.items.filter(
      (c) => c.chequeId === restoredParkedOrderMeta?.id,
    );

    if (parsedOrder.orderEasyId) {
      cart.setEasyId(parsedOrder.orderEasyId);
    }

    if (cheque?.userId) {
      cart.setUserId(cheque.userId);
    }

    if (restoredParkedOrderMeta?.membershipDiscountPercentage) {
      cart.setAppliedDiscount({
        id: MEMBERSHIP_DISCOUNT_ID,
        type: DISCOUNT_TYPE.PERCENTAGE,
        value: restoredParkedOrderMeta?.membershipDiscountPercentage,
      });
    }

    // Restore items
    orderItems.forEach((orderItem) => {
      // Check if the item has been deleted in the back office
      const item = allItems.find((i) => i.id === orderItem.itemId);

      if (!item) {
        return;
      }

      const itemWithCourseType = allItems.find((c) => c.id === orderItem.itemId);
      const variationsWithoutPrice = orderItem.richVariations.map((v) => ({
        ...v,
        price: 0,
      }));
      const removedVariationsWithoutPrice = orderItem.removedVariations.map((v) => ({
        ...v,
        price: 0,
      }));
      const price = orderItem.amount;

      addItem(
        orderItem.itemId,
        orderItem.title,
        itemToCategoryMap[orderItem.itemId]?.id ?? '',
        {
          removedVariations: removedVariationsWithoutPrice,
          variations: variationsWithoutPrice,
          courseType: itemWithCourseType?.courseType,
          isMessage: false,
          message: orderItem.message,
        },
        price,
      );
    });

    // Restore service charge
    if (cheque?.serviceCharge) {
      const serviceChargeItem = allItems.find((c) => c.id === cheque.serviceCharge?.itemId);
      if (serviceChargeItem) {
        addItem(
          serviceChargeItem.id,
          i18n(serviceChargeItem.titleLang),
          itemToCategoryMap[serviceChargeItem.id]?.id ?? '',
          {
            removedVariations: [],
            variations: [],
            courseType: serviceChargeItem?.courseType,
            isMessage: false,
          },
        );
      }
    }

    /* If the ticket was already printed, don't print it again on */
    if (cheque?.printed && setOrderWasBonged) {
      setOrderWasBonged(true);
    }

    if (parsedOrder.desiredTime) {
      cart.setDesiredTime(parsedOrder.desiredTime);
    }

    cart.updateServiceType(orderServiceType);
    orderParking.remove(restoredParkedOrderMeta?.id, '', true);
  }, [restoredParkedOrderMeta?.id, bongQuery.loading, allItems, isActive]);

  if (bongQuery.loading) {
    return (
      <LoadingDataScreen
        yOffset={yOffset}
        animateOnMount={animateOnMount}
        progress={progress}
        progressText={progressText}
        onClose={onClose}
      />
    );
  }

  // Hide if the company is not using app orders or if the setting showIncomingAppOrders is set to false
  // Otherwise we show the app orders button only if there are pending online orders, or if an auto accept was recently made (incomingAppOrders.isAutoAccepting)
  // Other times the Mini-KDS is shown instead
  const showAppOrdersButton =
    licenses.hasApp &&
    (storage.showIncomingAppOrders ||
      selectedKDSStation?.orderStatuses.includes(ORDER_STATUS.PENDING)) &&
    (incomingAppOrders.pendingOrdersIds.length > 0 || incomingAppOrders.isAutoAccepting);

  return (
    <Wrapper key="wrapper" yOffset={yOffset} animateOnMount={animateOnMount}>
      {reportSnapshot.loading && <LoadingScreen />}
      <Content>
        {(statusOfDay.hasDayStarted || !location.href.includes('/tables')) && (
          <Sidebar>
            <>
              <IconButton //Activate when search functionality is done
                icon={
                  <Icon
                    src={SearchIcon}
                    color={
                      statusOfDay.hasDayStarted
                        ? 'var(--colorBlueGray600)'
                        : 'var(--colorBlueGray1000)'
                    }
                  />
                }
                onClick={() => {
                  if (statusOfDay.hasDayStarted) {
                    toggleShowSearchModal();
                  }
                }}
              />
              <CountSelector count={count} onChange={setCount} yOffset={yOffset} />
              {showAppOrdersButton && <IncomingOrdersIndicator />}

              {/* Mini-KDS function, for companies using KDS but not having a specific KDS only screen */}
              {!showAppOrdersButton && <KDSButtonModal />}
              <SystemStatus />
              <PrinterStatus sendTestBong={sendTestBong} />
              <SidebarButtons />
            </>
          </Sidebar>
        )}

        {!statusOfDay.hasDayStarted ? (
          <DayNotStarted />
        ) : (
          <>
            <CategoriesList
              categories={categories}
              selectedCategoryId={selectedCategory?.id ?? null}
              handleCategoryClick={handleCategoryClick}
            />

            {breadcrumbs.length > 1 && (
              <Breadcrumbs
                breadcrumbs={breadcrumbs}
                handleClick={(index) => handleBreadcrumbClick(index)}
              />
            )}

            <GridButtons
              layout={layout}
              allItems={allItems}
              venueId={venueId}
              items={cart.items}
              selectedCategory={selectedCategory?.id ?? null}
              alreadyInCartCountMap={alreadyInCartCountMap}
              setSelectedVariationItemId={setSelectedVariationItemId}
              categoryItems={categoryItems}
              count={count}
              subPage={subPage}
              setSubPage={handleSubPage}
              mapItem={mapItem}
              addItem={addItem}
              addItems={addItems}
              addMessageOnLastItem={addMessageOnLastItem}
              itemToCategoryMap={itemToCategoryMap}
              data={data}
              currentActionId={currentActionId}
              breadcrumbs={breadcrumbs}
              serviceType={serviceType}
              hasServiceCharge={hasServiceCharge}
            />

            <BottomButtons
              venueId={venueId}
              isTsr={isTsr}
              showCourseTypeWait={Boolean(tableId) && hasCourseTypes}
              undo={undo}
              parkOrder={toggleShowParkname}
              itemsInCart={Boolean(cart.items.length)}
            />
          </>
        )}

        {showModifyModal && variationsItem && (
          <ItemModificationChange
            itemId={variationsItem.itemId}
            itemToRemove={variationsItem.id}
            allItems={allItems}
            onAddItem={addItem}
            closeModal={() => {
              setShowModifyModal(false);
            }}
            itemToCategoryMap={itemToCategoryMap}
            prevSelected={{
              variations: variationsItem?.variations ?? [],
              removedVariations: variationsItem?.removedVariations ?? [],
              upsales: cart.items.filter(
                (item) => item.parentId == variationsItem.id && item.id !== variationsItem.id,
              ),
            }}
          />
        )}
      </Content>

      <Cart
        showMessageModal={showMessageModal}
        setShowMessageModal={(val: boolean) => setShowMessageModal(val)}
        onClearCart={clear}
        venueId={venueId}
        paymentId={paymentId}
        offers={data?.company.offers || []}
        buttons={buttons}
        tableId={tableId!}
        selectedHasVariations={variationsItem?.hasModificationGroups ?? false}
        showModifyModal={() => setShowModifyModal(true)}
        isTsr={isTsr}
      />
      <Modal isShowing={showParkname} hide={toggleShowParkname}>
        <MessageInput
          type="text"
          placeHolder={i18n('ParkedOrders.Name.Placeholder')}
          acceptEmpty
          onSubmit={handleParkOrder}
          width="500px"
        />
      </Modal>

      <Modal isShowing={parkedPrintsModal.isShowing} backdropClose={false}>
        <ParkedOrderPrintsModal
          chequeId={newParkedOrderId}
          order={
            ({
              id: orderId!,
              createdAt: new Date().toISOString(),
              staffId,
              items: cart.packagedItems,
              tableId: null,
              venueId,
              serviceType,
              orderEasyId: cart.easyId,
              desiredTime: cart.desiredTime ?? null,
              userId: cart.userId,
            } as unknown) as OrderWithItems
          }
          onContinue={() => {
            cart.setDesiredTime(null);
            reset && reset();
            parkedPrintsModal.toggle();
          }}></ParkedOrderPrintsModal>
      </Modal>

      <Modal
        noPadding={true}
        position={'flex-start'}
        backdropClose={true}
        isShowing={showSearchModal}
        hide={toggleShowSearchModal}>
        <SearchArticles
          layout={layout}
          allItems={allItems}
          venueId={venueId}
          items={cart.items}
          mapItem={mapItem}
          addItem={addItem}
          itemToCategoryMap={itemToCategoryMap}
          toggleModal={toggleShowSearchModal}
        />
      </Modal>
    </Wrapper>
  );
};

export default React.memo(Bong);
